/* eslint-disable no-dupe-class-members */
import type { PollStorage, PollStoredState } from 'components-machine';
import lz from 'lz-string';

/** Объект для работы с хранилищем local storage */
export class PollsLocalStorage implements PollStorage {
  private key: string;

  /**
   * Создание хранилища
   * @param {string} key - ключ для local storage
   */
  constructor(key: string) {
    this.key = key;
  }

  /**
   * Получение состояния прохождения из хранилища.
   * @param {string} [key] - свойство состояния, значение которого будет возвращено.
   */
  public get(): Promise<Partial<PollStoredState> | null>;
  public get<TKey extends keyof PollStoredState>(key: TKey): Promise<PollStoredState[TKey] | null>;
  // eslint-disable-next-line @typescript-eslint/require-await
  public async get<TKey extends keyof PollStoredState>(key?: TKey) {
    try {
      const dataString = localStorage.getItem(this.key);

      if (!dataString) {
        return null;
      }

      const data = this.parse(dataString);

      if (!data) {
        console.error('No data');
        return null;
      }

      return key ? data[key] ?? null : data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Обновляет состояние прохождения в хранилище
   * @param {object} data - состояние прохождение опроса, возможно использование неполного состояния - остальные будут взяты из предыдущего состояния
   */
  public async update(data: Partial<PollStoredState>): Promise<void> {
    try {
      const previousData = await this.get();
      const dataToSet = { ...previousData, ...data };

      const stringifiedData = this.stringify(dataToSet);
      localStorage.setItem(this.key, stringifiedData);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Удалить состояние прохождения опроса
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  public async remove(): Promise<void> {
    try {
      localStorage.removeItem(this.key);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Cериализовать данные состояния прохождения
   * @param {object} data - состояние прохождения опроса
   */
  private stringify(data: Partial<PollStoredState>): string {
    return lz.compressToUTF16(JSON.stringify(data));
  }

  /**
   * Десериализовать данные состояния прохождения
   * @param {string} dataString - сериализованные данные
   */
  private parse(dataString: string): Partial<PollStoredState> | null {
    const jsonData = lz.decompressFromUTF16(dataString);

    if (!jsonData) {
      console.error('No data');
      return null;
    }

    const data = JSON.parse(jsonData) as Partial<PollStoredState> | null;

    return data;
  }
}
